'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TourState, TourStatePatch } from 'pc-types';

import { authHeaders, unwrapLambdaResponse } from '@/lib/api';
import { useAccessToken } from '@/lib/auth-client';
import { BACKEND_URL } from '@/lib/constants';
import { reportError } from '@/lib/report-error';
import { applyOptimisticTourStatePatch } from './tour-state-reducer';

export const TOUR_STATE_QUERY_KEY = ['tour-state'] as const;

export async function requestTourState(
  getAccessToken: () => Promise<string | undefined>,
  patch?: TourStatePatch
): Promise<TourState> {
  const token = await getAccessToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${BACKEND_URL}/preferences/tour`, {
    method: patch ? 'PATCH' : 'GET',
    headers: patch ? authHeaders(token) : { Authorization: `Bearer ${token}` },
    ...(patch ? { body: JSON.stringify(patch) } : {}),
  });

  if (!response.ok) {
    throw new Error(`Tour state request failed (${response.status})`);
  }

  return unwrapLambdaResponse<TourState>(await response.json());
}

export function useTourState() {
  const queryClient = useQueryClient();
  const { getAccessToken, loading: authLoading } = useAccessToken();

  const query = useQuery({
    queryKey: TOUR_STATE_QUERY_KEY,
    queryFn: () => requestTourState(getAccessToken),
    enabled: !authLoading,
    staleTime: 5 * 60_000,
  });

  const mutation = useMutation({
    mutationKey: ['tour-state', 'update'],
    scope: { id: 'tour-state' },
    mutationFn: (patch: TourStatePatch) =>
      requestTourState(getAccessToken, patch),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: TOUR_STATE_QUERY_KEY });
      const previous =
        queryClient.getQueryData<TourState>(TOUR_STATE_QUERY_KEY);
      queryClient.setQueryData<TourState>(
        TOUR_STATE_QUERY_KEY,
        applyOptimisticTourStatePatch(previous, patch)
      );
      return { previous };
    },
    onError: (error, _patch, context) => {
      reportError(error, { site: 'tour_state_update' });
      if (context?.previous) {
        queryClient.setQueryData(TOUR_STATE_QUERY_KEY, context.previous);
      } else {
        queryClient.removeQueries({
          queryKey: TOUR_STATE_QUERY_KEY,
          exact: true,
        });
      }
    },
    onSuccess: (state) => {
      queryClient.setQueryData(TOUR_STATE_QUERY_KEY, state);
    },
  });

  return {
    tourState: query.data,
    isLoading: authLoading || query.isLoading,
    isError: query.isError,
    updateTourState: mutation.mutate,
    updateTourStateAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
  };
}
